import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React from 'react';

/** Map-pin marker: teardrop shape with white center circle, label in white rounded rectangle below */
function PlanMarker({ label, color, top, left }: { label: string; color: string; top: string; left: string }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top,
        left,
        transform: 'translate(-50%, 0)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))',
      }}
    >
      {/* Teardrop pin body with central white circle */}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50% 50% 50% 0',
          transform: 'rotate(-45deg)',
          bgcolor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: '#fff',
            transform: 'rotate(45deg)',
          }}
        />
      </Box>
      {/* Label: white rounded rectangle, black text */}
      <Box
        sx={{
          mt: 0.75,
          px: 1,
          py: 0.25,
          borderRadius: '6px',
          bgcolor: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          border: '1px solid #E0E0E0',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12,
            fontWeight: 600,
            color: '#000',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

const UPCOMING_CAMERA_LOCATION_MESSAGE =
  'Upcoming feature — this will be available when the new camera module is available.';

export function CameraLocationTab() {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 280,
        bgcolor: '#E8EAED',
        position: 'relative',
        borderRadius: 0,
        m: 1.5,
        border: '1px solid #D1D5DB',
        overflow: 'hidden',
      }}
    >
      {/* Plan + markers (dimmed by overlay below) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: '#F8F9FA',
          zIndex: 0,
        }}
      >
        <PlanMarker label="L1" color="#1976D2" top="18%" left="50%" />
        <PlanMarker label="L2" color="#1976D2" top="42%" left="72%" />
        <PlanMarker label="L3" color="#1976D2" top="42%" left="28%" />
        <PlanMarker label="Cam031" color="#ED6C02" top="58%" left="50%" />
      </Box>

      {/* Grey-out veil over the map (popup stays clear above) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          bgcolor: 'rgba(55, 65, 81, 0.42)',
          pointerEvents: 'none',
        }}
      />

      {/* Popup-style notice above the plan */}
      <Paper
        elevation={6}
        role="status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 4,
          maxWidth: 'min(420px, calc(100% - 24px))',
          px: 2,
          py: 1.5,
          borderRadius: '10px',
          border: '1px solid #E3E8EF',
          bgcolor: '#FAFBFF',
          boxShadow:
            '0 4px 24px rgba(41, 50, 229, 0.12), 0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <InfoOutlinedIcon
            sx={{
              fontSize: 22,
              color: '#2932E5',
              flexShrink: 0,
              mt: 0.125,
            }}
            aria-hidden
          />
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 13,
              lineHeight: 1.45,
              fontWeight: 500,
              color: '#1E2939',
            }}
          >
            {UPCOMING_CAMERA_LOCATION_MESSAGE}
          </Typography>
        </Box>
        {/* Small “tail” so it reads like a popup above the canvas */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: -8,
            transform: 'translateX(-50%) rotate(45deg)',
            width: 14,
            height: 14,
            bgcolor: '#FAFBFF',
            borderRight: '1px solid #E3E8EF',
            borderBottom: '1px solid #E3E8EF',
            boxShadow: '2px 2px 4px rgba(0,0,0,0.04)',
          }}
        />
      </Paper>
    </Box>
  );
}
